import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bell,
  ClipboardList,
  Dog,
  ExternalLink,
  FileText,
  Home,
  Inbox as InboxIcon,
  ListChecks,
  LogOut,
  Mail,
  Menu,
  MessageSquare,
  Paintbrush,
  Search,
  Settings,
  Store,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../../lib/useDocumentTitle';
import { adminApi, type Overview } from '../../lib/adminApi';
import { ADMIN_OVERVIEW_REFRESH_EVENT } from '../../lib/adminEvents';
import { CmsProvider, useCms } from './CmsContext';
import { Login } from './Login';

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: (overview: Overview) => number;
  immersive?: boolean;
};

type NavGroup = { label?: string; items: NavItem[] };

const NAV_GROUPS: NavGroup[] = [
  { items: [{ to: '/admin', label: 'Home', icon: Home }] },
  {
    label: 'Leads',
    items: [
      { to: '/admin/applications', label: 'Applications', icon: ClipboardList, badge: (overview) => overview.counts.newApplications },
      { to: '/admin/inbox', label: 'Inbox', icon: InboxIcon, badge: (overview) => overview.counts.unreadEmails },
    ],
  },
  { label: 'Breeding', items: [{ to: '/admin/litters', label: 'Litters', icon: Dog }] },
  {
    label: 'Online store',
    items: [
      { to: '/admin/design', label: 'Design', icon: Paintbrush, immersive: true },
      { to: '/admin/content', label: 'Site content', icon: FileText },
      { to: '/admin/form', label: 'Application form', icon: ListChecks },
    ],
  },
  { label: 'Marketing', items: [{ to: '/admin/newsletter', label: 'Newsletter', icon: Mail }] },
];

const SETTINGS_ITEM: NavItem = { to: '/admin/settings', label: 'Settings', icon: Settings };
const ALL_NAV = [...NAV_GROUPS.flatMap((group) => group.items), SETTINGS_ITEM];

const PAGE_LABELS: Record<string, string> = {
  '/admin': 'Home',
  '/admin/applications': 'Applications',
  '/admin/inbox': 'Inbox',
  '/admin/litters': 'Litters',
  '/admin/content': 'Site content',
  '/admin/form': 'Application form',
  '/admin/newsletter': 'Newsletter',
  '/admin/settings': 'Settings',
};

export function AdminLayout() {
  useDocumentTitle('CMS | Sugarloaf Goldens');
  const [status, setStatus] = useState<'loading' | 'in' | 'out'>('loading');

  const check = useCallback(() => {
    adminApi.getSession()
      .then((session) => setStatus(session.authenticated ? 'in' : 'out'))
      .catch(() => setStatus('out'));
  }, []);

  useEffect(check, [check]);

  if (status === 'loading') return <div className="cms-loading"><span className="cms-spinner" />Loading Sugarloaf…</div>;
  if (status === 'out') return <Login onAuthenticated={() => setStatus('in')} />;

  return (
    <CmsProvider>
      <Shell onSignedOut={() => setStatus('out')} />
    </CmsProvider>
  );
}

function Shell({ onSignedOut }: { onSignedOut: () => void }) {
  const { dirty, saving, loaded, save, discardChanges } = useCms();
  const navigate = useNavigate();
  const location = useLocation();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [toast, setToast] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const notificationButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const refresh = () => { void adminApi.getOverview().then(setOverview).catch(() => undefined); };
    refresh();
    window.addEventListener(ADMIN_OVERVIEW_REFRESH_EVENT, refresh);
    return () => window.removeEventListener(ADMIN_OVERVIEW_REFRESH_EVENT, refresh);
  }, [saving]);

  useEffect(() => {
    setMobileOpen(false);
    setQuery('');
    setSearchOpen(false);
    setNotificationsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!notificationsOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!notificationRef.current?.contains(event.target as Node)) setNotificationsOpen(false);
    };
    window.addEventListener('pointerdown', closeOutside);
    return () => window.removeEventListener('pointerdown', closeOutside);
  }, [notificationsOpen]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(''), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const onSave = useCallback(async () => {
    setToast(await save() ? 'Changes saved and published.' : 'Could not save. Check your connection and try again.');
  }, [save]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        if (dirty && !saving) void onSave();
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        document.getElementById('cms-global-search')?.focus();
      }
      if (event.key === 'Escape') {
        setSearchOpen(false);
        setMobileOpen(false);
        if (notificationsOpen) window.requestAnimationFrame(() => notificationButtonRef.current?.focus());
        setNotificationsOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dirty, notificationsOpen, onSave, saving]);

  const signOut = async () => {
    if (dirty && !window.confirm('You have unsaved changes. Sign out and discard them?')) return;
    await adminApi.logout().catch(() => undefined);
    onSignedOut();
    navigate('/admin');
  };

  const normalizedQuery = query.trim().toLowerCase();
  const searchResults = useMemo(
    () => normalizedQuery ? ALL_NAV.filter((item) => item.label.toLowerCase().includes(normalizedQuery)).slice(0, 6) : [],
    [normalizedQuery],
  );
  const pageLabel = PAGE_LABELS[location.pathname] ?? 'Sugarloaf';
  const needsCmsState = ['/admin', '/admin/content', '/admin/form', '/admin/litters'].includes(location.pathname);
  const isInbox = location.pathname.startsWith('/admin/inbox');
  const notificationItems = [
    { to: '/admin/inbox', label: 'Email inbox', detail: 'Unread conversations', icon: InboxIcon, count: overview?.counts.unreadEmails ?? 0 },
    { to: '/admin/applications', label: 'Applications', detail: 'New puppy applications', icon: ClipboardList, count: overview?.counts.newApplications ?? 0 },
    { to: '/admin/inbox?folder=contacts', label: 'Website inquiries', detail: 'Messages from the contact form', icon: MessageSquare, count: overview?.counts.websiteInquiries ?? 0 },
    { to: '/admin/inbox?folder=drafts', label: 'Draft emails', detail: 'Saved replies and messages', icon: FileText, count: overview?.counts.draftEmails ?? 0 },
  ];
  const notificationAlertCount = (overview?.counts.unreadEmails ?? 0) + (overview?.counts.newApplications ?? 0);

  const navItem = (item: NavItem) => {
    const Icon = item.icon;
    const badge = overview ? item.badge?.(overview) ?? 0 : 0;
    const contents = (
      <>
        <Icon size={17} strokeWidth={1.9} aria-hidden="true" />
        <span>{item.label}</span>
        {badge > 0 && <b className="cms-badge">{badge > 99 ? '99+' : badge}</b>}
      </>
    );
    return item.immersive ? (
      <a key={item.to} href={item.to} className="cms-nav-link">{contents}</a>
    ) : (
      <NavLink key={item.to} to={item.to} end={item.to === '/admin'} className={({ isActive }) => `cms-nav-link${isActive ? ' active' : ''}`}>
        {contents}
      </NavLink>
    );
  };

  return (
    <div className="cms">
      <a className="cms-skip" href="#cms-main-content">Skip to content</a>
      {mobileOpen && <button type="button" className="cms-sidebar-backdrop" aria-label="Close navigation" onClick={() => setMobileOpen(false)} />}

      <aside id="cms-navigation" className={`cms-sidebar ${mobileOpen ? 'is-open' : ''}`}>
        <div className="cms-sidebar-head">
          <Link to="/admin" className="cms-brand" aria-label="Sugarloaf admin home">
            <span className="cms-brand-mark">S</span>
            <span><b>Sugarloaf</b><small>Goldens</small></span>
          </Link>
          <button type="button" className="cms-icon-btn cms-sidebar-close" aria-label="Close navigation" onClick={() => setMobileOpen(false)}><X size={19} /></button>
        </div>

        <a className="cms-store-switcher" href="/admin/design">
          <span><Store size={17} aria-hidden="true" /></span>
          <span><b>Online store</b><small>Live</small></span>
          <ExternalLink size={14} aria-hidden="true" />
        </a>

        <nav className="cms-nav" aria-label="Admin navigation">
          {NAV_GROUPS.map((group, index) => (
            <div className="cms-nav-group" key={group.label ?? `main-${index}`}>
              {group.label && <p>{group.label}</p>}
              {group.items.map(navItem)}
            </div>
          ))}
        </nav>

        <div className="cms-sidebar-foot">
          {navItem(SETTINGS_ITEM)}
          <div className="cms-account">
            <span className="cms-account-avatar">SG</span>
            <span><b>Sugarloaf Goldens</b><small>Administrator</small></span>
            <button type="button" className="cms-icon-btn" onClick={signOut} aria-label="Sign out"><LogOut size={17} /></button>
          </div>
        </div>
      </aside>

      <main className={`cms-main${isInbox ? ' cms-main-mail' : ''}`}>
        <header className="cms-topbar">
          <button type="button" className="cms-icon-btn cms-menu-button" aria-label="Open navigation" aria-controls="cms-navigation" aria-expanded={mobileOpen} onClick={() => setMobileOpen(true)}><Menu size={20} /></button>
          <span className="cms-page-context">{pageLabel}</span>
          <div className="cms-search-wrap" onFocus={() => { setNotificationsOpen(false); setSearchOpen(true); }} onBlur={() => window.setTimeout(() => setSearchOpen(false), 100)}>
            <form className="cms-search" onSubmit={(event) => {
              event.preventDefault();
              const first = searchResults[0];
              if (!first) return;
              if (first.immersive) window.location.assign(first.to);
              else navigate(first.to);
            }}>
              <Search size={17} aria-hidden="true" />
              <input id="cms-global-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search admin" aria-label="Search admin" autoComplete="off" />
              <kbd>⌘ K</kbd>
            </form>
            {searchOpen && normalizedQuery && (
              <div className="cms-search-results">
                {searchResults.length ? searchResults.map((item) => {
                  const Icon = item.icon;
                  return item.immersive ? (
                    <a key={item.to} href={item.to}><Icon size={16} />{item.label}<ExternalLink size={13} /></a>
                  ) : (
                    <Link key={item.to} to={item.to}><Icon size={16} />{item.label}</Link>
                  );
                }) : <p>No admin pages found</p>}
              </div>
            )}
          </div>
          <div className="cms-topbar-actions">
            <a className="cms-view-store" href="/" target="_blank" rel="noreferrer"><span>View store</span><ExternalLink size={15} /></a>
            <div className="cms-notification-wrap" ref={notificationRef}>
              <button
                type="button"
                className={`cms-icon-btn cms-notification ${notificationsOpen ? 'is-open' : ''}`}
                ref={notificationButtonRef}
                aria-label={notificationAlertCount ? `Notifications, ${notificationAlertCount} new items` : 'Notifications'}
                aria-controls="cms-notification-menu"
                aria-expanded={notificationsOpen}
                onClick={() => { setSearchOpen(false); setNotificationsOpen((open) => !open); }}
              >
                <Bell size={18} />
                {notificationAlertCount > 0 && <span className="cms-notification-badge">{notificationAlertCount > 99 ? '99+' : notificationAlertCount}</span>}
              </button>
              {notificationsOpen && (
                <section id="cms-notification-menu" className="cms-notification-menu" aria-label="Notifications">
                  <header><span><b>Notifications</b><small>{notificationAlertCount ? `${notificationAlertCount} new item${notificationAlertCount === 1 ? '' : 's'}` : 'Everything new is up to date'}</small></span></header>
                  <nav aria-label="Notification inboxes">
                    {notificationItems.map(({ to, label, detail, icon: Icon, count }) => (
                      <Link key={to} to={to} onClick={() => setNotificationsOpen(false)}>
                        <span className="cms-notification-menu-icon"><Icon size={16} aria-hidden="true" /></span>
                        <span><b>{label}</b><small>{detail}</small></span>
                        <strong className={count ? '' : 'is-zero'}>{count}</strong>
                      </Link>
                    ))}
                  </nav>
                </section>
              )}
            </div>
          </div>
        </header>

        <div id="cms-main-content" className={`cms-content${isInbox ? ' cms-content-mail' : ''}`}>
          {loaded || !needsCmsState ? <Outlet /> : <div className="cms-page cms-page-loading" aria-label="Loading editor"><span className="cms-skeleton cms-skeleton-title" /><span className="cms-skeleton cms-skeleton-card" /></div>}
        </div>
      </main>

      {dirty && (
        <div className="cms-savebar" role="region" aria-label="Unsaved changes">
          <div><span className="cms-savebar-dot" /><b>Unsaved changes</b><small>Save to publish updates across the site.</small></div>
          <div className="cms-savebar-actions">
            <button type="button" className="cms-btn cms-btn-inverse" disabled={saving} onClick={discardChanges}>Discard</button>
            <button type="button" className="cms-btn cms-btn-light" disabled={saving} onClick={() => void onSave()}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}

      {toast && <div className="cms-toast" role="status">{toast}</div>}
    </div>
  );
}
